import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCreativePreferencesStore } from './store';
import { bootstrap, fetchPreview, patchSection, submitBrief } from './api';
import { emptyDoc, groupOf } from './emptyDoc';
import {
  getBriefIds, isPreview, isReadOnly, setPayloadReadOnly, setPreviewMode, setSession,
} from './session';

const TEXT_DEBOUNCE_MS = 800;

// Module-scoped so the debounce timers survive re-renders without a context.
// Internal to the retry logic; nothing outside this module reads them.
const pendingText = {};
let retryTimer = null;

/**
 * Reads the brief out of the app-lifetime Redux slice and returns the actions
 * that write to it. The form's structure comes from `cpSections`, straight from
 * the server: order, titles, options and caps are never declared here.
 */
export const usePreferences = ({ bookingId, briefUuid, readOnly } = {}) => {
  const {
    cpDoc, cpSections, cpFormVersion, cpBookingName, cpLoading, cpSaveState, cpSection, cpTab, cpEgComment, cpRound, cpRoundDate, cpLocksAt, cpReopenRequested, cpCanEdit, cpDenied, cpDeniedCode,
    setCpDoc, setCpSections, setCpFormVersion, setCpBookingName, setCpLoading, setCpSaveState, setCpSection, setCpTab, setCpEgComment, setCpRound, setCpRoundDate, setCpLocksAt, setCpReopenRequested, setCpCanEdit, setCpDenied, setCpDeniedCode,
  } = useCreativePreferencesStore();

  // Set before anything below reads it, so the route component's own call sees
  // the same values every other call site will.
  if (bookingId !== undefined) setSession({ bookingId, briefUuid, readOnly });

  const doc = cpDoc || emptyDoc();
  // Memoised so the fallback does not make a new array every render.
  const sections = useMemo(() => cpSections || [], [cpSections]);
  // `submitted` is the client's own lock; read-only is ops looking. Both
  // disable every input, but only `submitted` blocks navigating backwards.
  const submitted = !!doc.submitted;
  // Three ways in: the client submitted, ops is looking, or the deadline passed.
  const closed = !!doc.locked;
  const locked = submitted || closed || isReadOnly();

  const docRef = useRef(doc);
  docRef.current = doc;

  // --- flow, derived entirely from the payload ---------------------------

  const index = Math.max(0, sections.findIndex((s) => s.key === cpSection));
  const section = sections[index] || null;
  const prevKey = index > 0 ? sections[index - 1].key : null;
  const nextKey = index < sections.length - 1 ? sections[index + 1]?.key : null;

  // --- persistence -------------------------------------------------------

  const push = useCallback(
    (nextDoc, sectionKey) => {
      setCpSaveState('saving');
      if (retryTimer) clearTimeout(retryTimer);

      const attempt = (n) =>
        patchSection({
          ...getBriefIds(),
          sectionKey,
          payload: groupOf(nextDoc, sectionKey),
          doc: nextDoc,
        })
          .then(() => setCpSaveState('idle'))
          .catch((err) => {
            if (err && err.rejected) {
              // The server refused this write. Retrying sends the same body, so
              // the backoff below would only repeat the refusal.
              if (err.code === 'brief_submitted' || err.code === 'brief_locked') {
                // Stale local state: the form is closed server-side, either by
                // the client or by the deadline. Close it here too rather than
                // reporting a save failure.
                const shut = err.code === 'brief_locked'
                  ? { locked: true }
                  : { submitted: true };
                setCpDoc({ ...docRef.current, ...shut });
                setCpSaveState('idle');
                return;
              }
              setCpSaveState('rejected');
              return;
            }
            setCpSaveState('error');
            // 1s / 3s / 8s backoff. Local state is never discarded.
            const delays = [1000, 3000, 8000];
            if (n >= delays.length) return;
            retryTimer = setTimeout(() => attempt(n + 1), delays[n]);
          });

      attempt(0);
    },
    [setCpSaveState, setCpDoc]
  );

  // `sectionKey` and `fieldKey` are the server's own keys, used verbatim as the
  // storage keys in the PATCH body.
  const write = useCallback(
    (sectionKey, fieldKey, value, debounced) => {
      if (docRef.current.submitted || isReadOnly()) return;
      const stamped = {
        ...docRef.current,
        [sectionKey]: { ...groupOf(docRef.current, sectionKey), [fieldKey]: value },
        revision: docRef.current.revision + 1,
        current_section: sectionKey,
        section_updated_at: {
          ...docRef.current.section_updated_at,
          [sectionKey]: new Date().toISOString(),
        },
      };
      docRef.current = stamped;
      setCpDoc(stamped);

      if (!debounced) {
        push(stamped, sectionKey);
        return;
      }
      if (pendingText[sectionKey]) clearTimeout(pendingText[sectionKey]);
      pendingText[sectionKey] = setTimeout(() => {
        delete pendingText[sectionKey];
        push(docRef.current, sectionKey);
      }, TEXT_DEBOUNCE_MS);
    },
    [push, setCpDoc]
  );

  const flushText = useCallback(() => {
    Object.keys(pendingText).forEach((key) => {
      clearTimeout(pendingText[key]);
      delete pendingText[key];
      push(docRef.current, key);
    });
  }, [push]);

  // --- actions -----------------------------------------------------------

  // Selections save on tap. `maxPicks` is the server's cap, shown to the user;
  // the server enforces it too.
  const togglePick = useCallback(
    (sectionKey, fieldKey, id, maxPicks) => {
      const list = groupOf(docRef.current, sectionKey)[fieldKey] || [];
      // Older answers may hold numbers where options now carry strings.
      const has = list.some((x) => String(x) === String(id));
      if (!has && maxPicks && list.length >= maxPicks) return;
      const next = has ? list.filter((x) => String(x) !== String(id)) : [...list, id];
      write(sectionKey, fieldKey, next);
    },
    [write]
  );

  // A single-value answer. Pressing the active choice again clears it, so a
  // mis-tap is recoverable without a separate reset control.
  const setValue = useCallback(
    (sectionKey, fieldKey, value) => {
      const current = groupOf(docRef.current, sectionKey)[fieldKey];
      write(sectionKey, fieldKey, String(current) === String(value) ? '' : value);
    },
    [write]
  );

  const setText = useCallback(
    (sectionKey, fieldKey, value) => write(sectionKey, fieldKey, value, true),
    [write]
  );

  const goTo = useCallback(
    (key, tabIndex = 0) => {
      if (!key) return;
      flushText();
      setCpSection(key);
      // Walking forwards opens a section on its first tab; walking backwards
      // enters on its last, so the flow reverses exactly as it advanced.
      setCpTab(tabIndex);
      const scroller = document.querySelector('.cp-content');
      if (scroller) scroller.scrollTop = 0;
    },
    [flushText, setCpSection, setCpTab]
  );

  const lock = useCallback(
    (extra) => {
      const stamped = { ...docRef.current, submitted: true, ...extra };
      docRef.current = stamped;
      setCpDoc(stamped);
      return stamped;
    },
    [setCpDoc]
  );

  // Locks only once the server has accepted. Locking optimistically would leave
  // a rejected form read-only with no way back.
  const submit = useCallback(
    (acknowledged) => {
      // Nothing to submit, and locking would leave ops looking at a dead form.
      if (isPreview()) return Promise.resolve(null);
      flushText();
      return submitBrief({ ...getBriefIds(), doc: docRef.current, acknowledged })
        .then((res) => {
          lock({ acknowledged: true });
          return res;
        })
        .catch((err) => {
          // Already submitted elsewhere: that is a lock, not a failure.
          if (err && err.code === 'brief_submitted') {
            lock();
            return null;
          }
          // The deadline passed mid session. Closed, but not submitted.
          if (err && err.code === 'brief_locked') {
            const shut = { ...docRef.current, locked: true };
            docRef.current = shut;
            setCpDoc(shut);
            return null;
          }
          throw err;
        });
    },
    [flushText, lock, setCpDoc]
  );

  return useMemo(
    () => ({
      doc,
      sections,
      section,
      index,
      tab: cpTab || 0,
      setTab: setCpTab,
      prevKey,
      nextKey,
      formVersion: cpFormVersion,
      bookingName: cpBookingName || '',
      egComment: cpEgComment || '',
      // Not null only while a past round is on screen.
      round: cpRound,
      roundDate: cpRoundDate || '',
      loading: cpLoading,
      saveState: cpSaveState,
      locked,
      closed,
      locksAt: cpLocksAt || null,
      reopenRequested: cpReopenRequested || null,
      setReopenRequested: setCpReopenRequested,
      readOnly: isReadOnly(),
      // Why it is read-only, never whether: the backend is the gate.
      canEdit: cpCanEdit,
      denied: cpDenied,
      deniedCode: cpDeniedCode,
      goTo,
      togglePick,
      setValue,
      setText,
      flushText,
      submit,
      setCpDoc, setCpSections, setCpFormVersion, setCpBookingName, setCpLoading, setCpSection,
      setCpEgComment, setCpRound, setCpRoundDate, setCpLocksAt, setCpCanEdit,
      setCpDenied, setCpDeniedCode,
    }),
    [doc, sections, section, index, cpTab, setCpTab, prevKey, nextKey, cpFormVersion, cpBookingName, setCpBookingName, cpEgComment, cpRound, cpRoundDate, cpLoading, cpSaveState, locked, closed, cpLocksAt, cpReopenRequested, setCpReopenRequested, cpCanEdit, setCpCanEdit, cpDenied, setCpDenied, cpDeniedCode, setCpDeniedCode, goTo, togglePick, setValue, setText, flushText, submit, setCpDoc, setCpSections, setCpFormVersion, setCpLoading, setCpSection, setCpEgComment, setCpRound, setCpRoundDate, setCpLocksAt]
  );
};

/** Loads the form into the store. Call once, from the route component. */
export const useBootstrapPreferences = ({ bookingId, briefUuid, readOnly, snapshot }) => {
  const {
    setCpDoc, setCpSections, setCpFormVersion, setCpBookingName, setCpLoading, setCpSection,
    setCpEgComment, setCpRound, setCpRoundDate, setCpLocksAt, setCpCanEdit,
    setCpDenied, setCpDeniedCode, setReopenRequested, flushText,
  } =
    usePreferences({ bookingId, briefUuid, readOnly });

  useEffect(() => {
    let alive = true;
    setCpLoading(true);
    setCpDenied(false);
    bootstrap({ bookingId, briefUuid, snapshot })
      .then((res) => {
        if (!alive) return;
        // The brief is not this visitor's to read. Nothing else is set: the
        // payload carries no sections to render.
        if (res.denied) {
          setCpDenied(true);
          setCpDeniedCode(res.code || '');
          setCpLoading(false);
          return;
        }
        setCpSections(res.sections);
        setCpFormVersion(res.form_version);
        // Absent, not empty, when ops preview a draft: there is no booking.
        setCpBookingName(res.booking_name || '');
        setCpEgComment(res.eg_comment || '');
        // A past round is history: the payload says so, and every write guard
        // reads the same flag the staff view uses.
        setPayloadReadOnly(res.read_only);
        // Absent on a preview payload, which keeps its editable behaviour.
        setCpCanEdit(res.can_edit !== false);
        setCpRound(res.round_number ?? null);
        const round = (res.submissions || []).find(
          (entry) => entry.round === res.round_number
        );
        setCpRoundDate(round ? round.submitted_at : '');
        setCpLocksAt(res.locks_at || null);
        setReopenRequested(res.reopen_requested || null);
        setCpDoc(res.answers);
        // Resume where they left off, falling back to the first section.
        const resume = res.sections.some((s) => s.key === res.current_section)
          ? res.current_section
          : res.sections[0]?.key;
        setCpSection(resume || null);
        setCpLoading(false);
      })
      .catch(() => alive && setCpLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, briefUuid, snapshot]);

  useEffect(() => {
    const onHide = () => flushText();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      onHide();
    };
  }, [flushText]);
};

/**
 * Loads a form version for ops to read before it is published. There is no
 * booking and no brief behind it, so nothing is saved: the preview flag blocks
 * every write, and clearing it on unmount keeps the real form unaffected.
 */
export const useBootstrapPreview = ({ version }) => {
  const {
    setCpDoc, setCpSections, setCpFormVersion, setCpBookingName, setCpLoading, setCpSection,
    setCpEgComment, setCpRound, setCpRoundDate,
  } = usePreferences({ bookingId: null, briefUuid: 'preview', readOnly: false });
  const [error, setError] = useState(null);
  const [versionState, setVersionState] = useState('');

  useEffect(() => {
    let alive = true;
    setCpLoading(true);
    setPreviewMode(true);
    // A past round's banner belongs to the real form; a preview has neither.
    setPayloadReadOnly(false);
    setCpRound(null);
    setCpRoundDate('');

    fetchPreview({ version })
      .then((res) => {
        if (!alive) return;
        setPreviewMode(res.preview !== false);
        setVersionState(res.version_state || '');
        setCpSections(res.sections);
        setCpFormVersion(res.form_version);
        // Absent, not empty, when ops preview a draft: there is no booking.
        setCpBookingName(res.booking_name || '');
        setCpEgComment(res.eg_comment || '');
        setCpDoc(res.answers);
        setCpSection(res.sections[0]?.key || null);
        setCpLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setCpLoading(false);
      });

    return () => {
      alive = false;
      // Module-scoped: leaving it set would block writes on the real form.
      setPreviewMode(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  return { error, versionState };
};
